/**
 * Extraire les lignes de performance d'un bloc du dashboard, avant de l'envoyer à l'analyse.
 *
 * **Pourquoi ne pas envoyer le bloc entier.** Les réponses du dashboard portent, en plus de leurs
 * lignes, des séries jour par jour, des métadonnées et des données brutes que l'analyse n'exploite
 * pas : mesuré, la charge complète des treize blocs pesait **4 Mo** pour une poignée de tableaux
 * — des mégaoctets à téléverser à chaque question, depuis n'importe quelle connexion.
 *
 * **Pourquoi la détection reste générique.** Aucun des blocs n'a le même format : les lignes
 * vivent tantôt dans `adGroups`, tantôt dans `countries`, `audiences`, `topProducts` ou
 * `charts[0].data`. En revanche les noms de métriques sont communs à tous. On cherche donc le
 * plus grand tableau dont les éléments en portent, sans rien savoir du bloc — un composant
 * nouveau est pris en charge sans qu'on écrive une ligne.
 *
 * Le serveur refait ce travail de son côté, car il doit rester tolérant aux autres appelants.
 * Ici, c'est la taille de la requête qu'on borne ; là-bas, celle du prompt.
 */

/**
 * Les métriques qui identifient une ligne de performance.
 *
 * ⚠️ **Les cinq premières sont celles des régies ; les écrans analytiques en emploient d'autres.**
 * Une ligne de pages d'atterrissage ne porte ni `clicks` ni `cost` mais `visits`, `pageViews`,
 * `bounceRate` ; un article acheté porte `quantity` et `orders` ; un événement `nb_events`. Sans
 * ces noms, ces tableaux n'étaient pas reconnus comme des lignes et l'analyse ne les recevait
 * jamais — elle ne voyait que les blocs qui, par chance, portent `conversions` et
 * `conversionsValue` sous les noms de GA4.
 */
const METRIQUES = [
  // Régies
  'impressions', 'clicks', 'cost', 'conversions', 'conversionsValue',
  // Analytique — noms harmonisés (GA4, Piano, Matomo côté service)
  'visits', 'visitors', 'pageViews', 'bounceRate', 'avgTimeOnSite', 'actionsPerVisit',
  'transactions', 'totalRevenue', 'revenue', 'sessions', 'users',
  // Analytique — noms bruts de Matomo, que certains rapports gardent
  'nb_visits', 'nb_actions', 'nb_uniq_visitors', 'nb_events',
  // Achats e-commerce
  'quantity', 'orders',
  // Événements : GA4 nomme `eventCount` ce que Matomo appelle `nb_events`, et la liste doit
  // connaître les deux — sinon le bloc Événements n'est pas reconnu comme un tableau de lignes
  // et l'analyse ne le reçoit pas.
  'eventCount', 'eventValue', 'eventsPerSession'
];

/** Deux métriques numériques suffisent à reconnaître une ligne. */
const estUneLigne = (valeur) =>
  !!valeur
  && typeof valeur === 'object'
  && !Array.isArray(valeur)
  && METRIQUES.filter((m) => typeof valeur[m] === 'number').length >= 2;

/** Les champs qui trahissent un point de série temporelle, et non une ligne de tableau. */
const CHAMPS_DATE = ['date', 'dateRange', 'day_of_month', 'segmentDate'];

/** Les champs qui peuvent porter l'intitulé d'une ligne. */
const CHAMPS_INTITULE = [
  'name', 'title', 'audienceName', 'audienceSegment', 'country', 'device', 'network',
  'category', 'categoryName', 'searchTerm', 'term', 'keyword', 'day', 'dayOfWeek',
  'ageRange', 'gender', 'label', 'campaignName',
  // Écrans analytiques : chaque répartition nomme sa dimension à sa façon.
  'deviceLabel', 'browser', 'browserLabel', 'language', 'languageLabel',
  'landingPage', 'landingPageLabel', 'item', 'itemLabel', 'page', 'pageLabel',
  'continent', 'region', 'city', 'source', 'medium', 'channel',
  'weekday', 'weekdayLabel', 'hour', 'eventCategory', 'eventAction', 'eventName'
];

const aUnIntitule = (ligne) =>
  CHAMPS_INTITULE.some((c) => typeof ligne[c] === 'string' && ligne[c].trim());

/**
 * Ce point appartient-il à une série temporelle ?
 *
 * C'est la distinction qui manquait : les réponses portent **à la fois** le tableau affiché et
 * la série jour par jour qui alimente le graphe. En retenant simplement le plus grand tableau,
 * on ramenait les quatre cents points quotidiens au lieu des trois appareils.
 */
const estUnPointDeSerie = (ligne) => CHAMPS_DATE.some((c) => ligne[c] !== undefined);

/** Parcourt un objet en profondeur bornée et empile tous les tableaux de lignes métriques. */
function collecter(valeur, profondeur, sortie) {
  if (!valeur || typeof valeur !== 'object' || profondeur > 4) return;

  if (Array.isArray(valeur)) {
    if (valeur.length && estUneLigne(valeur[0])) { sortie.push(valeur); return; }
    for (const element of valeur.slice(0, 20)) collecter(element, profondeur + 1, sortie);
    return;
  }

  for (const sous of Object.values(valeur)) collecter(sous, profondeur + 1, sortie);
}

/**
 * Le tableau de lignes à retenir dans un bloc.
 *
 * On collecte tous les tableaux de lignes métriques, puis on **préfère ceux qui ressemblent à
 * un tableau de l'écran** : des lignes intitulées, qui ne sont pas des points de série. À
 * défaut, on retombe sur le plus grand — mieux vaut une donnée imparfaite que rien.
 */
function trouverLignes(valeur) {
  const candidats = [];
  collecter(valeur, 0, candidats);
  if (candidats.length === 0) return null;

  const estUnTableau = (lignes) =>
    lignes.some(aUnIntitule) && !lignes.every(estUnPointDeSerie);

  const tableaux = candidats.filter(estUnTableau);
  const retenus = tableaux.length > 0 ? tableaux : candidats;
  return retenus.reduce((a, b) => (b.length > a.length ? b : a));
}

/**
 * Le bloc réduit à ses lignes, prêt pour l'envoi — ou `null` s'il n'en contient aucune.
 *
 * Le plafond de 300 lignes vaut pour les blocs à gros volume (termes de recherche, produits) :
 * le serveur n'en retiendra de toute façon qu'une dizaine, regroupées et classées par dépense.
 * Il ne coupe donc rien d'utile, il évite seulement de téléverser le reste.
 */
/**
 * Les totaux d'un bloc, quand il n'a pas de lignes.
 *
 * **Les écrans analytiques ne sont pas faits comme les régies.** Google Analytics, Piano et
 * Matomo portent leurs chiffres de tête dans un objet (`currentPeriod`, `previousPeriod`) et non
 * dans un tableau. `lignesDuBloc` n'y trouvait rien et les déclarait absents : l'analyse voyait
 * la répartition par pays mais pas le trafic, et concluait qu'il n'y avait rien à dire.
 *
 * On ne retient que les valeurs simples — nombres et chaînes courtes. Un objet imbriqué est soit
 * une série déjà couverte par `lignesDuBloc`, soit une configuration sans intérêt ici.
 */
export function totauxDuBloc(bloc) {
  if (!bloc || typeof bloc !== 'object' || Array.isArray(bloc)) return null;

  const totaux = {};
  for (const [cle, valeur] of Object.entries(bloc)) {
    if (typeof valeur === 'number') totaux[cle] = valeur;
    else if (typeof valeur === 'string' && valeur.length <= 60) totaux[cle] = valeur;
  }
  return Object.keys(totaux).length > 0 ? totaux : null;
}

/**
 * Les blocs d'un dashboard analytique, à plat, prêts à être parcourus un par un.
 *
 * ## Le défaut qu'elle corrige
 *
 * Les trois réponses — GA4, Matomo, Piano — ont la même forme : quelques clés au premier
 * niveau (`currentPeriod`, `charts`, `success`) et **tous les blocs sous `data`** — `summary`,
 * `daily`, `channels`, `geography`, `devices`, `pages`, `events`…
 *
 * La charge d'analyse bouclait sur le premier niveau. La seule entrée exploitable y était donc
 * `data` **en entier**, et `lignesDuBloc` — qui ne rend qu'un tableau, le plus grand — y
 * choisissait la géographie. L'analyse recevait un bloc sur neuf et concluait, à juste titre,
 * qu'elle n'avait que la répartition par pays sous les yeux.
 *
 * ## Pourquoi réunir les deux niveaux plutôt que descendre d'un cran
 *
 * Parce que les deux portent quelque chose : `data` tient les tableaux, le premier niveau tient
 * les totaux de période. Ne garder que `data` priverait l'analyse des chiffres de tête ; ne
 * garder que le premier niveau est le défaut qu'on répare.
 *
 * `data` lui-même est écarté de la sortie : ses blocs y figurent déjà un par un, et le laisser
 * ferait analyser deux fois les mêmes lignes. `success` aussi, qui n'est pas une donnée.
 */
export function blocsAnalysables(reponse) {
  if (!reponse || typeof reponse !== 'object') return {};

  const { data, success, ...premierNiveau } = reponse;
  return {
    ...premierNiveau,
    ...(data && typeof data === 'object' && !Array.isArray(data) ? data : {})
  };
}

export function lignesDuBloc(bloc, maximum = 300) {
  const lignes = trouverLignes(bloc);
  if (!lignes || lignes.length === 0) return null;
  return { lignes: lignes.slice(0, maximum) };
}

export default lignesDuBloc;
